import React from 'react';
import {sanitize} from 'dompurify';
import FileUploader from './FileUploader';
import validators from "../../../utils/forms/validators";


export class Field extends React.PureComponent {
	render() {
		return (
			<div
				className={`field ${this.props.className || ''} ${
					this.props.addOns && this.props.addOns.length ? 'has-addons' : ''
				}`}
			>
				{this.props.children}
			</div>
		);
	}
}

export class Control extends React.PureComponent {
	render() {
		return (
			<div className={`control ${this.props.controlClasses || ''}`}>
				{this.props.children}
			</div>
		);
	}
}

export class Label extends React.PureComponent {

	render() {
		return this.props.label ? (
			<div className="pb-1">
				<label
					htmlFor={this.props.dataKey || this.props.label}
					className={`label is-size-6 has-text-primary ${
						this.props.labelClasses || ''
					}`}
					style={this.props.type == 'checkbox' ? { marginTop: '-5px' } : {}}
				>
					{this.props.label}
					{this.props.subLabel && (
						React.isValidElement(this.props.subLabel) ?
							<div className="label is-size-7 has-text-grey mt-0"
							>{this.props.subLabel}</div> :
							<div className="label is-size-7 has-text-grey mt-0"
								 dangerouslySetInnerHTML={{ __html: sanitize(this.props.subLabel) }}
							/>
					)}
				</label>
			</div>
		) : (
			<></>
		);
	}
}

class ErrorMessage extends React.PureComponent {}

export default class Input extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			hasError: false,
			hasSuccess: false,
			isFocused: false,
			passwordIsVisible: false
		};

		this.validatorTimeout = null;
	}

	static VALIDATION_TIMEOUT_LENGTH = 800;

	static VALIDATE_ON_MOUNT_TIMEOUT = 1000;

	componentDidMount() {
		if (this.props.validator && this.props.validateOnMount) {
			this.validatorTimeout = setTimeout(() => {
				if (this.props.validator(this.props.value)) {
					this.setState({ hasSuccess: true });
					this.props.onSuccess && this.props.onSuccess();
				}
			}, Input.VALIDATE_ON_MOUNT_TIMEOUT);
		}
	}

	// need to be able to force error from props.
	hasError = () => {
		return this.props.hasError || this.state.hasError;
	}

	handleChange = (ev) => {
		let val;
		if (ev.target.type === 'checkbox') {
			val = ev.target.checked;
		} else {
			val = ev.target.value;
		}

		if (this.state.hasError || this.state.hasSuccess) {
			this.setState({ hasError: false, hasSuccess: false });
		}
		// handle passed changes based on pass function
		if (this.props.onChange) this.props.onChange(val);

		// handle validation if function passed
		if (this.props.validator) {
			clearTimeout(this.validatorTimeout);
			this.validatorTimeout = setTimeout(() => {
				if (!this.props.validator(val)) {
					this.setState({ hasError: true });
					if (this.props.onError) this.props.onError();
				} else {
					this.setState({ hasSuccess: true });
					this.props.onSuccess && this.props.onSuccess();
				}
			}, Input.VALIDATION_TIMEOUT_LENGTH);
		}
	};

	onFocus = (e) => {
		this.setState({ isFocused: true });
	};

	onBlur = (e) => {
		this.setState({ isFocused: false });
	};

	renderOptions() {
		if (!this.props.options || !Array.isArray(this.props.options)) return [];
		let options = this.props.options.reduce((r, d, i, a) => {
			r.push(
				<option value={d.value} key={d.value} disabled={!!d.disabled}>
					{d.display}
				</option>
			);
			return r;
		}, []);

		options.unshift(
			<option
				value=""
				key="placeholder"
				disabled
				className="has-text-grey-light"
				selected
			>
				{this.props.placeholder || '--'}
			</option>
		);

		return options;
	}

	getInputClassNames = () => {
		return `${this.props.inputClasses || ''} ${
			this.state.hasSuccess ? 'is-success' : ''
		} ${this.hasError() ? 'is-danger' : ''}`;
	};

	// allows for the application of a formatter without altering the props value on TEXT FIELDS ONL.
	getFormattedValue = () => {
		if (this.props.formatter && !this.state.isFocused) {
			return this.props.formatter(this.props.value);
		}
		return this.props.value;
	};

	render() {
		let { type, className } = this.props;
		switch (type) {
			case 'textarea':
				return (
					<Field {...this.props}>
						<Control {...this.props}>
							<Label {...this.props} />
							<textarea
								className={`textarea ${this.getInputClassNames()}`}
								id={this.props.dataKey}
								aria-label={this.props.label}
								onChange={this.handleChange}
								value={this.props.value}
								rows={this.props.rows || 2}
								placeholder={this.props.placeholder || '...'}
							/>
						</Control>
					</Field>
				);

			case 'select':
				return (
					<Field {...this.props}>
						<Label {...this.props} />
						<Control {...this.props}>
							<div className={`select ${this.getInputClassNames()} `}>
								<select
									className={this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									onChange={this.handleChange}
									value={this.props.value}
								>
									{this.renderOptions()}
								</select>
							</div>
						</Control>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'checkbox':
				return (
					<Field {...this.props}>
						<div className="is-flex">
							<input
								type="checkbox"
								id={this.props.dataKey}
								aria-label={this.props.label}
								className={`mr-3 ${this.getInputClassNames()}`}
								onChange={this.handleChange}
								checked={[null, undefined].includes(this.props.value) ? (this.props.defaultChecked || false) : this.props.value }
								defaultChecked={this.props.defaultChecked || false}
							/>
							<Label {...this.props} />
						</div>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'password':
				return (
					<Field {...this.props}>
						<Label {...this.props} />
						<Control {...this.props} controlClasses={'has-icons-right ' + this.props.controlClasses}>
							<input
								className={'input ' + this.getInputClassNames()}
								id={this.props.dataKey}
								aria-label={this.props.label}
								type={this.state.passwordIsVisible ? "text" : "password"}
								onChange={this.handleChange}
								value={this.props.value || ''}
								placeholder={this.props.placeholder}
								onBlur={this.props.onBlur || (() => null)}
							/>
							<span
								className="icon is-right is-clickable"
								onClick={() => {
									this.setState({passwordIsVisible: !this.state.passwordIsVisible})
								}}
							>
								<i className={`far ${this.state.passwordIsVisible ? 'fa-eye-slash' : 'fa-eye'}`}/>
							</span>
						</Control>
						{!!this.props.addOns && this.props.addOns}
						{!!this.hasError() && (
							<p className="help is-danger">{this.props.errorText}</p>
						)}
					</Field>
				);

			case 'file':
				return <FileUploader {...this.props} />;

			case 'date':
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								style={
									this.props.addOns && this.props.addOns.length
										? {
												display: 'flex',
												width: '100%',
										  }
										: {}
								}
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="date"
									onChange={this.handleChange}
									value={this.getFormattedValue() || new Date()}
									placeholder={this.props.placeholder}
								/>
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);


			case 'hex_color':
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								controlClasses="has-icons-right"
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="text"
									onChange={this.handleChange}
									value={this.getFormattedValue() || ''}
									placeholder={this.props.placeholder}

								/>
								<span className="icon is-right">
							  		<i className="fas fa-square fa-2x" style={{color: this.getFormattedValue()}}/>
								</span>
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				)

			case 'text':
			default:
				return (
					<>
						<Label {...this.props} />
						<Field {...this.props}>
							<Control
								{...this.props}
								style={{display: 'flex', width: '100%'}}
							>
								<input
									onFocus={this.onFocus}
									onBlur={this.onBlur}
									className={'input ' + this.getInputClassNames()}
									id={this.props.dataKey}
									aria-label={this.props.label}
									type="text"
									onChange={this.handleChange}
									value={this.getFormattedValue() || ''}
									placeholder={this.props.placeholder}
									disabled={this.props.disabled || false}
								/>
								{validators.isValidHex.validator(this.getFormattedValue()) && (
									<div
										style={{
											width: '20px',
											height: '20px',
											backgroundColor: this.getFormattedValue(),
											display: 'inline-block',
											marginLeft: '10px',
										}}
									/>
								)}
							</Control>
							{!!this.props.addOns && this.props.addOns}
							{!!this.hasError() && (
								<p className="help is-danger">{this.props.errorText}</p>
							)}
						</Field>
					</>
				);
		}
	}
}
